import { CheckmarkFilled } from '@carbon/icons-react';
import { Button, Spin } from 'antd';
import classNames from 'classnames';
import dayjs from 'dayjs';
import Card from 'components/Card/Card';
import { PROVIDER_STATUSES } from 'constants';
import ProviderImportStatusBanner, { getImportStatusValue } from './ProviderImportStatusBanner';

const TERMINAL_IMPORT_STATUSES = ['SUCCESS', 'DUPE_FOUND', 'FAILURE', 'RETRYABLE'];

const getLatestImportRecord = (importStatusTrail) => {
  if (!Array.isArray(importStatusTrail) || !importStatusTrail.length) {
    return null;
  }

  return importStatusTrail[0];
};

const formatSyncDate = (record) => {
  const date = record?.createdAt || record?.lastModifiedDate || record?.createdDate;
  return date ? dayjs(date).format('MMM D, YYYY [at] h:mm A') : null;
};

const ReviewActions = ({ application, importStatusTrail, loadingImportStatus, syncingImportStatus, onSyncImport }) => {
  const currentStatus = application?.status?.status?.title || application?.status?.status?.description;
  const latestImportRecord = getLatestImportRecord(importStatusTrail);
  const latestImportStatus = getImportStatusValue(latestImportRecord);
  const isReviewed = currentStatus === PROVIDER_STATUSES.REVIEWED;
  const isTerminal = TERMINAL_IMPORT_STATUSES.includes(latestImportStatus);
  const syncDate = latestImportStatus === 'SUCCESS' ? formatSyncDate(latestImportRecord) : null;

  return (
    <Card title="Legacy Import Status">
      {loadingImportStatus ? (
        <div className="flex items-center gap-2 text-gray-600">
          <Spin size="small" />
          <span>Checking Legacy import status</span>
        </div>
      ) : latestImportStatus ? (
        <div className="space-y-4">
          <ProviderImportStatusBanner importStatus={latestImportRecord} />

          {latestImportStatus === 'SUCCESS' && (
            <div className="rounded-lg border border-solid border-green-200 bg-green-50 p-4">
              <div className="flex items-center gap-2 font-medium text-green-700">
                <CheckmarkFilled className="h-5 w-5" />
                <span>Synced to Legacy</span>
              </div>
              {syncDate && <div className="mt-2 text-sm text-green-700">Completed {syncDate}</div>}
              {latestImportRecord?.legacyApplicationId && (
                <div className="mt-2 text-sm text-green-700">
                  Legacy Application ID: {latestImportRecord.legacyApplicationId}
                </div>
              )}
            </div>
          )}

          {!isTerminal && (
            <div className="text-sm text-gray-500">
              This status will update automatically after Legacy processing completes.
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <div
            className={classNames(
              'rounded-lg border border-solid p-4 text-sm',
              isReviewed
                ? 'border-gray-200 bg-gray-50 text-gray-600'
                : 'border-yellow-300 bg-yellow-50 text-yellow-800',
            )}
          >
            {isReviewed
              ? 'Legacy import has not reported a status yet.'
              : 'Legacy import will start when the application status is changed to Reviewed.'}
          </div>

          {isReviewed && (
            <Button type="primary" loading={syncingImportStatus} onClick={onSyncImport}>
              Sync to Legacy
            </Button>
          )}
        </div>
      )}
    </Card>
  );
};

export default ReviewActions;
